import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LoginModal } from '../components/LoginModal';
import { getModelId } from '../components/ModelSelector';
import '../styles/pages.css';

export const Home = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('mechanical');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const navigate = useNavigate();
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const getSessionId = () => {
    try {
      let sid = localStorage.getItem('pinak_session_id');
      if (!sid) {
        sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('pinak_session_id', sid);
      }
      return sid;
    } catch (_) {
      return '';
    }
  };

  const handleQuickAction = (text: string) => {
    sessionStorage.setItem('pending_prompt', text);
    sessionStorage.setItem('pending_model', getModelId());
    const sid = getSessionId();
    navigate(`/viewer${sid ? `?session=${encodeURIComponent(sid)}` : ''}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() && uploadedImages.length === 0) return;

    if (uploadedImages.length > 0) {
      sessionStorage.setItem('pending_images', JSON.stringify(uploadedImages));
    }
    
    if (prompt.trim()) {
      sessionStorage.setItem('pending_prompt', prompt.trim());
    }
    
    sessionStorage.setItem('pending_model', getModelId());

    const sid = getSessionId();
    navigate(`/viewer${sid ? `?session=${encodeURIComponent(sid)}` : ''}`);
  };

  // Auto-resize textarea
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    const maxHeight = 200;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        if (dataUrl && dataUrl.startsWith('data:image')) {
          setUploadedImages((prev) => [...prev, dataUrl]);
        }
      };
      reader.onerror = () => {
        alert('Error reading image file');
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const features = {
    mechanical: [
      {
        title: 'Precision Gears & Drivetrain',
        description: 'Generate spur gears, helical gears, bevel gears, and complete drivetrain assemblies with accurate tooth profiles and specifications.',
        stat: '< 30 sec',
        label: 'Generation time',
        tryPrompt: 'Create a 24-tooth spur gear with module 2mm, 20mm thickness, and a 10mm center bore'
      },
      {
        title: 'Fasteners & Hardware',
        description: 'Create ISO and ANSI standard bolts, nuts, screws, washers, and custom fasteners with thread specifications.',
        stat: 'ISO/ANSI',
        label: 'Standards',
        tryPrompt: 'Design an M10 hex head bolt, 40mm length, with ISO standard threading'
      },
      {
        title: 'Brackets & Mounts',
        description: 'Design L-brackets, mounting plates, motor mounts, and structural components with parametric hole patterns.',
        stat: 'Parametric',
        label: 'Fully editable',
        tryPrompt: 'Create an L-bracket with 100mm x 60mm arms, 5mm thickness, and 4 mounting holes (M6)'
      }
    ],
    electronics: [
      {
        title: 'PCB Enclosures',
        description: 'Generate custom enclosures for Raspberry Pi, Arduino, ESP32, and any custom PCB with precise mounting points.',
        stat: '100+ boards',
        label: 'Supported',
        tryPrompt: 'Design a Raspberry Pi 4 enclosure with snap-fit lid, GPIO access slot, and ventilation holes'
      },
      {
        title: 'Ventilation & Cooling',
        description: 'Add ventilation slots, fan mounts, and thermal management features to any enclosure design.',
        stat: 'Optimized',
        label: 'Airflow',
        tryPrompt: 'Create a 40mm fan mount bracket with honeycomb ventilation pattern and M3 mounting holes'
      },
      {
        title: 'Cable Management',
        description: 'Create cable clips, routing channels, and strain relief components for clean installations.',
        stat: 'Custom',
        label: 'Dimensions',
        tryPrompt: 'Design a cable clip for 8mm diameter cables with adhesive mounting base'
      }
    ],
    structural: [
      {
        title: 'Extrusion Profiles',
        description: 'Design aluminum extrusion-compatible brackets, corner connectors, and T-slot accessories.',
        stat: '2020/2040',
        label: 'Profile support',
        tryPrompt: 'Create a 90-degree corner bracket for 2020 aluminum extrusion with M5 T-slot nuts'
      },
      {
        title: 'Frame Components',
        description: 'Create frame joints, cross-members, and structural elements for custom frame assemblies.',
        stat: 'FEA Ready',
        label: 'Analysis',
        tryPrompt: 'Design a 3-way corner joint for 25mm square tube frame with welding tabs'
      },
      {
        title: 'Panel Mounts',
        description: 'Generate panel brackets, hinges, and door mechanisms for enclosure systems.',
        stat: 'Modular',
        label: 'Design',
        tryPrompt: 'Create a piano hinge mount for 3mm acrylic panel with 50mm length'
      }
    ],
    export: [
      {
        title: 'STL for 3D Printing',
        description: 'Export watertight STL files optimized for FDM and SLA 3D printing with correct wall thickness.',
        stat: '.STL',
        label: 'Format',
        tryPrompt: 'Create a phone stand with 70-degree viewing angle, optimized for 3D printing'
      },
      {
        title: 'STEP for CAM',
        description: 'Export STEP files compatible with CNC machining software and professional CAD systems.',
        stat: '.STEP',
        label: 'Format',
        tryPrompt: 'Design a CNC-machinable aluminum fixture plate 150x100x20mm with 4 clamping slots'
      },
      {
        title: 'IGES Universal',
        description: 'Generate IGES files for maximum compatibility with legacy CAD/CAM systems.',
        stat: '.IGES',
        label: 'Format',
        tryPrompt: 'Create a mechanical shaft coupling for 10mm to 8mm shafts with set screw holes'
      }
    ]
  };

  const faqs = [
    {
      question: 'What is Pinak?',
      answer: 'Pinak is an AI-powered CAD design platform that transforms natural language descriptions into production-ready 3D models. Using advanced spatial intelligence, Pinak understands engineering context and generates accurate mechanical parts, assemblies, and technical drawings in seconds.'
    },
    {
      question: 'What types of designs can Pinak create?',
      answer: 'Pinak can generate a wide range of mechanical components including gears, fasteners, brackets, enclosures, structural elements, and custom parts. It supports parametric design, so you can easily modify dimensions and specifications through conversation.'
    },
    {
      question: 'What file formats does Pinak export?',
      answer: 'Pinak exports industry-standard formats including STL (optimized for 3D printing), STEP (for CNC machining and professional CAD), and IGES (for universal compatibility). All exports are production-ready with correct tolerances and specifications.'
    },
    {
      question: 'Can I upload sketches or reference images?',
      answer: 'Yes! Pinak accepts hand-drawn sketches, technical drawings, and reference images. Our vision AI interprets your sketches and converts them into precise 3D models, preserving your design intent while adding engineering rigor.'
    },
    {
      question: 'How accurate are the generated models?',
      answer: 'Pinak generates models with engineering-grade accuracy. Gear tooth profiles follow standard involute curves, threads conform to ISO/ANSI specifications, and all dimensions maintain parametric precision suitable for manufacturing.'
    }
  ];

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Header
        user={user}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onSignOut={handleSignOut}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={() => {
          setIsLoginModalOpen(false);
        }}
      />

      <main className="home-main">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <h1>CAD design for engineers</h1>
            <p className="hero-description">
              Turn your ideas into production ready 3D models.
            </p>
            <div className="hero-actions">
              <Link to="/viewer" className="btn-primary">
                Start Designing
              </Link>
              <Link to="/use-cases" className="btn-trustpilot">
                View Examples
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Prompt Section */}
        <section className="prompt-section">
          <div className="prompt-card">
            <div className="prompt-card-inner">
              <form onSubmit={handleSubmit} className="prompt-form">
                <div className="prompt-input-area">
                  <textarea
                    id="promptInput"
                    placeholder="Describe a part, assembly, or mechanism..."
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      autoResizeTextarea(e.target);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    rows={1}
                  />
                  {uploadedImages.length > 0 && (
                    <div className="image-preview-container">
                      {uploadedImages.map((img, idx) => (
                        <div key={idx} className="image-preview-item">
                          <img src={img} alt={`Preview ${idx + 1}`} />
                          <button
                            type="button"
                            onClick={() => setUploadedImages((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="prompt-toolbar">
                  <div className="prompt-toolbar-left">
                    <input
                      type="file"
                      id="imageInput"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      ref={imageInputRef}
                      onChange={handleImageUpload}
                    />
                    <button
                      type="button"
                      className="toolbar-btn"
                      title="Upload sketch or reference image"
                      onClick={() => imageInputRef.current?.click()}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Image</span>
                    </button>
                  </div>
                  <button type="submit" className="submit-btn" aria-label="Generate">
                    <span>Generate</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </form>
            </div>
            
            <div className="suggestions">
              <span className="suggestions-label">Try these:</span>
              <div className="suggestion-chips">
                <button
                  className="suggestion-chip"
                  onClick={() => handleQuickAction('Design a 24-tooth involute spur gear with module 2mm, 14.5° pressure angle, 12mm thickness, and a 10mm center bore with keyway. Include chamfered edges for easier assembly.')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v4m0 14v4m11-11h-4M5 12H1m16.36-5.64l-2.83 2.83m-7.07 7.07l-2.83 2.83m0-12.73l2.83 2.83m7.07 7.07l2.83 2.83"/>
                  </svg>
                  24-tooth spur gear with keyway bore
                </button>
                <button
                  className="suggestion-chip"
                  onClick={() => handleQuickAction('Create an M10 hex socket head cap screw, 45mm length, with partial threading (25mm thread length). ISO 4762 standard with 8mm allen key socket.')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                  M10 socket head cap screw
                </button>
                <button
                  className="suggestion-chip"
                  onClick={() => handleQuickAction('Design an L-shaped mounting bracket with 80mm x 60mm arms, 4mm wall thickness, with 4 countersunk M5 mounting holes (2 per arm), 6mm corner radius fillets, and reinforcing gusset ribs.')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8" cy="8" r="1.5"/>
                    <circle cx="16" cy="8" r="1.5"/>
                    <circle cx="8" cy="16" r="1.5"/>
                    <circle cx="16" cy="16" r="1.5"/>
                  </svg>
                  L-bracket with gusset ribs
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <div className="features-container">
            <div className="features-header">
              <div className="features-header-text">
                <h2>Designing across all domains and use cases.</h2>
                <p>From mechanical parts to electronics enclosures, Pinak handles the full spectrum of engineering design with precision and speed.</p>
              </div>
              <Link to="/use-cases" className="view-all-link">
                View all capabilities
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
                </div>

            <div className="features-tabs">
              <button 
                className={`feature-tab ${activeTab === 'mechanical' ? 'active' : ''}`}
                onClick={() => setActiveTab('mechanical')}
              >
                Mechanical
              </button>
              <button 
                className={`feature-tab ${activeTab === 'electronics' ? 'active' : ''}`}
                onClick={() => setActiveTab('electronics')}
              >
                Electronics
              </button>
              <button 
                className={`feature-tab ${activeTab === 'structural' ? 'active' : ''}`}
                onClick={() => setActiveTab('structural')}
              >
                Structural
              </button>
              <button 
                className={`feature-tab ${activeTab === 'export' ? 'active' : ''}`}
                onClick={() => setActiveTab('export')}
              >
                Export
              </button>
              </div>

            <div className="features-grid">
              {features[activeTab as keyof typeof features].map((feature, index) => (
                <div key={index} className="feature-card">
                  <h3 className="feature-card-title">{feature.title}</h3>
                  <p className="feature-card-description">{feature.description}</p>
                  <div className="feature-card-footer">
                    <div className="feature-card-meta">
                      <span className="feature-card-stat">{feature.stat}</span>
                      <span className="feature-card-label">{feature.label}</span>
                    </div>
                    <button 
                      className="feature-card-btn"
                      onClick={() => handleQuickAction(feature.tryPrompt)}
                      title={`Try: ${feature.tryPrompt}`}
                    >
                      Try it
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="testimonial-section">
          <div className="testimonial-container">
            <div className="testimonial-content">
              <p className="testimonial-quote">
                "Using Pinak for our prototyping workflow cut our design iteration time by 80%. What used to take hours of CAD modeling now takes a single conversation."
              </p>
              <div className="testimonial-author">
                <span className="testimonial-name">Alex Chen</span>
                <span className="testimonial-role">Lead Mechanical Engineer, RoboTech</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="faq-container">
            <div className="faq-header">
              <h2>FAQs</h2>
            </div>

            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className={`faq-item ${openFaq === index ? 'open' : ''}`}
                >
                  <button 
                    className="faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="faq-question-text">{faq.question}</span>
                    <span className="faq-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                  <div className="faq-answer">
                    <p className="faq-answer-content">{faq.answer}</p>
                  </div>
            </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-container">
          <div className="cta-content">
              <h2>Join thousands of engineers designing with AI</h2>
            </div>
            <div className="cta-buttons">
              <Link to="/viewer" className="btn-primary">
                Start Designing
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};
